import { useTranslation } from "react-i18next";
import { Item } from "../../../../../interfaces/Commons/Items/items"
import GenericPromises from "../../../../../api/GenericPromises";
import useSnackBar from "../../../../../components/Commons/SnackBar/useSnackBar";
import { FormsErrors } from "../../../../../hooks/Forms/FormsErrors";
import { useEffect, useState } from "react";
import { ItemCost } from "../../../../../interfaces/Commons/Items/itemCosts";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Spinner } from "../../../../../components/Commons/Spinner/Spinner";
import { Box, Divider, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { ButtonLoading, myStylePickers, PrimaryButton } from "../../../../../theme/buttons";
import dayjs from "dayjs";
import { Menuitem } from "../../../../../interfaces/Security/menu";
import { usePermissions } from "../../../../../hooks/usePermissions";

interface ItemCosttabProps {
  itemPayload: Item | undefined,
}

export const ItemCostTab = ({
  itemPayload
}: ItemCosttabProps) => {
  const [t] = useTranslation("global");
  const { GenericPostResource, GenericGetResource, GenericPutResource } = GenericPromises();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { GetError } = FormsErrors();
  const { GetResourceByUrl } = usePermissions();
  const [loadingPost, setLoadingPost] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isEdit, setIsEdit] = useState(true);
  const [itemCostLocal, setItemCostLocal] = useState<ItemCost>();
  const [datePickerStates, setDatePickerStates] = useState({
    standard_cost_date: false,
    last_purchase_cost_date: false,
    average_cost_date: false,
    first_entries_date: false,
    first_outings_date: false,
    last_entries_date: false,
    last_outings_date: false,
  });
  const [defaultDateStandardCost, setDefaultDateStandardCost] = useState<Date | undefined>();
  const [defaultDateLastPurchaseCost, setDefaultDateLastPurchaseCost] = useState<Date | undefined>();
  const [defaultDateAverageCost, setDefaultDateAverageCost] = useState<Date | undefined>();
  const [defaultDateFirstEntries, setDefaultDateFirstEntries] = useState<Date | undefined>();
  const [defaultDateFirstOutings, setDefaultDateFirstOutings] = useState<Date | undefined>();
  const [defaultDateLastEntries, setDefaultDateLastEntries] = useState<Date | undefined>();
  const [defaultDateLastOutings, setDefaultDateLastOutings] = useState<Date | undefined>();

  const handleOpenDatePicker = (fieldName: string) => {
    setDatePickerStates(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleCloseDatePicker = (fieldName: string) => {
    setDatePickerStates(prev => ({ ...prev, [fieldName]: false }));
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    reset,
    trigger,
  } = useForm<ItemCost>({
    defaultValues: {}
  });
  const onSubmit: SubmitHandler<ItemCost> = (data) => onUpdateData(data);

  const onUpdateData = (data: ItemCost) => {
    trigger()
      .then((responseTrigger) => {
        if (responseTrigger) {
          if (data.item_cost_id) {
            let myData = {
              standard_cost_value: data.standard_cost_value,
              standard_cost_date: data.standard_cost_date,
              last_purchase_cost_value: data.last_purchase_cost_value ?? 0,
              last_purchase_cost_date: data.last_purchase_cost_date ?? null,
              average_cost_value: data.average_cost_value ?? 0,
              average_cost_date: data.average_cost_date ?? null,
              first_entries_value: data.first_entries_value ?? 0,
              first_entries_date: data.first_entries_date ?? null,
              first_outings_value: data.first_outings_value ?? 0,
              first_outings_date: data.first_outings_date ?? null,
              last_entries_value: data.last_entries_value ?? 0,
              last_entries_date: data.last_entries_date ?? null,
              last_outings_value: data.last_outings_value ?? 0,
              last_outings_date: data.last_outings_date ?? null,
              item_id: itemPayload?.item_id,
            }
            setLoadingPost(true);
            GenericPutResource("/itemcosts/" + itemCostLocal?.item_cost_id, myData)
              .then((response) => {
                setItemCostLocal(response.data);
                showSnackBar(t("generic.snackbar.update"), "success");
              })
              .catch((error) => {
                if (error.response && error.response.status === 409) {
                  showSnackBar(t(error.response.data), 'error');
                } else {
                  showSnackBar(error.message, 'error');
                }
              }).finally(() => setLoadingPost(false));
          }
          else {
            let myData = {
              standard_cost_value: data.standard_cost_value,
              standard_cost_date: data.standard_cost_date?.toISOString() ?? null,
              last_purchase_cost_value: data.last_purchase_cost_value,
              last_purchase_cost_date: data.last_purchase_cost_date?.toISOString() ?? null,
              average_cost_value: data.average_cost_value,
              average_cost_date: data.average_cost_date?.toISOString() ?? null,
              first_entries_value: data.first_entries_value,
              first_entries_date: data.first_entries_date?.toISOString() ?? null,
              first_outings_value: data.first_outings_value,
              first_outings_date: data.first_outings_date?.toISOString() ?? null,
              last_entries_value: data.last_entries_value,
              last_entries_date: data.last_entries_date?.toISOString() ?? null,
              last_outings_value: data.last_outings_value,
              last_outings_date: data.last_outings_date?.toISOString() ?? null,
              item_id: itemPayload?.item_id,
            }
            setLoadingPost(true);
            GenericPostResource("/itemcosts", myData)
              .then((response) => {
                setItemCostLocal(response.data);
                showSnackBar(t("generic.snackbar.update"), "success");
              })
              .catch((error) => {
                if (error.response && error.response.status === 409) {
                  showSnackBar(t(error.response.data), 'error');
                } else {
                  showSnackBar(error.message, 'error');
                }
              }).finally(() => setLoadingPost(false));
          }
        }
      });
  }

  const disableKeyboardEntry = (e: any) => {
    if (e?.preventDefault) {
      e?.preventDefault();
      e?.stopPropagation();
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      GetResourceByUrl(`/itemcosts`)
        .then((responsePermission: Menuitem) => {
          setIsEdit(!responsePermission.update);
        })
      await GenericGetResource(`/itemcosts/byitemid/${itemPayload?.item_id}`)
        .then((response) => {
          if (response.data.data.length > 0) {
            setItemCostLocal(response.data.data[0]);
            reset({
              ...response.data.data[0],
            });

            setDefaultDateStandardCost(response.data.data[0]?.standard_cost_date ? new Date(response.data.data[0]?.standard_cost_date) : undefined);
            setDefaultDateLastPurchaseCost(response.data.data[0]?.last_purchase_cost_date ? new Date(response.data.data[0]?.last_purchase_cost_date) : undefined);
            setDefaultDateAverageCost(response.data.data[0]?.average_cost_date ? new Date(response.data.data[0]?.average_cost_date) : undefined);
            setDefaultDateFirstEntries(response.data.data[0]?.first_entries_date ? new Date(response.data.data[0]?.first_entries_date) : undefined);
            setDefaultDateLastEntries(response.data.data[0]?.last_entries_date ? new Date(response.data.data[0]?.last_entries_date) : undefined);
            setDefaultDateFirstOutings(response.data.data[0]?.first_outings_date ? new Date(response.data.data[0]?.first_outings_date) : undefined);
            setDefaultDateLastOutings(response.data.data[0]?.last_outings_date ? new Date(response.data.data[0]?.last_outings_date) : undefined);
          }

        })
        .catch((error) => {
          showSnackBar(error.message, 'error');
        });
      setDataLoaded(true);
    }

    fetchData();
  }, []);

  return (
    <>
      {!dataLoaded && <Spinner isBox={false} />}
      {dataLoaded &&
        <Box className='d-flex justify-content-center' sx={{ minWidth: 950 }}>

          <form onSubmit={handleSubmit(onSubmit)} className='mt-4 w-100'>

            <div className='d-flex'>
              <div className='w-100'>
                <TextField
                  variant="filled"
                  label={`${t("items.title-view")}`}
                  value={`${itemPayload?.item_code} - ${itemPayload?.item_description}`}
                  size="small"
                  style={{ width: "100%" }}
                  disabled={true}
                />
              </div>

            </div>

            <Divider />

            <div className='d-flex mt-2'>
              <div className='w-50'>
                <Controller
                  name="standard_cost_value"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      type='number'
                      ref={field.ref}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 2,
                      }}
                      value={field.value}
                      label={`${t("itemcosts.fields.standard_cost_value")}`}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.standard_cost_value}
                      helperText={GetError(errors.standard_cost_value?.type)}
                      size="small"
                      disabled={isEdit}
                    />
                  }
                />
              </div>
              <div className="w-50">
                <Controller
                  name="standard_cost_date"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) =>
                    <DatePicker
                      open={datePickerStates.standard_cost_date}
                      onClose={() => handleCloseDatePicker('standard_cost_date')}
                      ref={field.ref}
                      label={`${t("itemcosts.fields.standard_cost_date")}`}
                      onChange={(day, _) => {
                        field.onChange(day);
                      }}
                      slotProps={{
                        popper: {
                          sx: myStylePickers
                        },
                        textField: {
                          variant: "filled",
                          size: "small",
                          fullWidth: true,
                          onClick: () => handleOpenDatePicker('standard_cost_date'),
                          onBeforeInput: disableKeyboardEntry,
                          error: !!errors.standard_cost_date,
                          helperText: GetError(errors.standard_cost_date?.type),
                        }
                      }}
                      defaultValue={defaultDateStandardCost ? dayjs((defaultDateStandardCost.toString())) : undefined}
                      format="DD/MM/YYYY"
                      disableFuture
                      disabled={isEdit}
                    />
                  }
                />
              </div>
            </div>
            <div className='d-flex mt-2'>
              <div className='w-50'>
                <Controller
                  name="last_purchase_cost_value"
                  control={control}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      type='number'
                      ref={field.ref}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 2,
                      }}
                      value={field.value}
                      label={`${t("itemcosts.fields.last_purchase_cost_value")}`}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.last_purchase_cost_value}
                      helperText={GetError(errors.last_purchase_cost_value?.type)}
                      size="small"
                      disabled={isEdit}
                    />
                  }
                />
              </div>
              <div className="w-50">
                <Controller
                  name="last_purchase_cost_date"
                  control={control}
                  render={({ field }) =>
                    <DatePicker
                      open={datePickerStates.last_purchase_cost_date}
                      onClose={() => handleCloseDatePicker('last_purchase_cost_date')}
                      ref={field.ref}
                      label={`${t("itemcosts.fields.last_purchase_cost_date")}`}
                      onChange={field.onChange}
                      slotProps={{
                        popper: {
                          sx: myStylePickers
                        },
                        textField: {
                          variant: "filled",
                          size: "small",
                          fullWidth: true,
                          onClick: () => handleOpenDatePicker('last_purchase_cost_date'),
                          onBeforeInput: disableKeyboardEntry,
                          error: !!errors.last_purchase_cost_date,
                          helperText: GetError(errors.last_purchase_cost_date?.type),
                        }
                      }}
                      format="DD/MM/YYYY"
                      disableFuture
                      disabled={isEdit}
                      defaultValue={defaultDateLastPurchaseCost ? dayjs((defaultDateLastPurchaseCost.toString())) : undefined}
                    />
                  }
                />
              </div>
            </div>
            <div className='d-flex mt-2'>
              <div className='w-50'>
                <Controller
                  name="average_cost_value"
                  control={control}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      type='number'
                      ref={field.ref}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 2,
                      }}
                      value={field.value}
                      label={`${t("itemcosts.fields.average_cost_value")}`}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.average_cost_value}
                      helperText={GetError(errors.average_cost_value?.type)}
                      size="small"
                      disabled={isEdit}
                    />
                  }
                />
              </div>
              <div className="w-50">
                <Controller
                  name="average_cost_date"
                  control={control}
                  render={({ field }) =>
                    <DatePicker
                      open={datePickerStates.average_cost_date}
                      onClose={() => handleCloseDatePicker('average_cost_date')}
                      ref={field.ref}
                      label={`${t("itemcosts.fields.average_cost_date")}`}
                      onChange={field.onChange}
                      slotProps={{
                        popper: {
                          sx: myStylePickers
                        },
                        textField: {
                          variant: "filled",
                          size: "small",
                          fullWidth: true,
                          onClick: () => handleOpenDatePicker('average_cost_date'),
                          onBeforeInput: disableKeyboardEntry,
                          error: !!errors.average_cost_date,
                          helperText: GetError(errors.average_cost_date?.type),
                        }
                      }}
                      format="DD/MM/YYYY"
                      disableFuture
                      disabled={isEdit}
                      defaultValue={defaultDateAverageCost ? dayjs((defaultDateAverageCost.toString())) : undefined}
                    />
                  }
                />
              </div>
            </div>
            <div className='d-flex mt-2'>
              <div className='w-50'>
                <Controller
                  name="first_entries_value"
                  control={control}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      type='number'
                      ref={field.ref}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 2,
                      }}
                      value={field.value}
                      label={`${t("itemcosts.fields.first_entries_value")}`}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.first_entries_value}
                      helperText={GetError(errors.first_entries_value?.type)}
                      size="small"
                      disabled={isEdit}
                    />
                  }
                />
              </div>
              <div className="w-50">
                <Controller
                  name="first_entries_date"
                  control={control}
                  render={({ field }) =>
                    <DatePicker
                      open={datePickerStates.first_entries_date}
                      onClose={() => handleCloseDatePicker('first_entries_date')}
                      ref={field.ref}
                      label={`${t("itemcosts.fields.first_entries_date")}`}
                      onChange={field.onChange}
                      slotProps={{
                        popper: {
                          sx: myStylePickers
                        },
                        textField: {
                          variant: "filled",
                          size: "small",
                          fullWidth: true,
                          onClick: () => handleOpenDatePicker('first_entries_date'),
                          onBeforeInput: disableKeyboardEntry,
                          error: !!errors.first_entries_date,
                          helperText: GetError(errors.first_entries_date?.type),
                        }
                      }}
                      format="DD/MM/YYYY"
                      disableFuture
                      disabled={isEdit}
                      defaultValue={defaultDateFirstEntries ? dayjs((defaultDateFirstEntries.toString())) : undefined}
                    />
                  }
                />
              </div>
            </div>
            <div className='d-flex mt-2'>
              <div className='w-50'>
                <Controller
                  name="first_outings_value"
                  control={control}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      type='number'
                      ref={field.ref}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 2,
                      }}
                      value={field.value}
                      label={`${t("itemcosts.fields.first_outings_value")}`}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.first_outings_value}
                      helperText={GetError(errors.first_outings_value?.type)}
                      size="small"
                      disabled={isEdit}
                    />
                  }
                />
              </div>
              <div className="w-50">
                <Controller
                  name="first_outings_date"
                  control={control}
                  render={({ field }) =>
                    <DatePicker
                      open={datePickerStates.first_outings_date}
                      onClose={() => handleCloseDatePicker('first_outings_date')}
                      ref={field.ref}
                      label={`${t("itemcosts.fields.first_outings_date")}`}
                      onChange={field.onChange}
                      slotProps={{
                        popper: {
                          sx: myStylePickers
                        },
                        textField: {
                          variant: "filled",
                          size: "small",
                          fullWidth: true,
                          onClick: () => handleOpenDatePicker('first_outings_date'),
                          onBeforeInput: disableKeyboardEntry,
                          error: !!errors.first_outings_date,
                          helperText: GetError(errors.first_outings_date?.type),
                        }
                      }}
                      format="DD/MM/YYYY"
                      disableFuture
                      disabled={isEdit}
                      defaultValue={defaultDateFirstOutings ? dayjs((defaultDateFirstOutings.toString())) : undefined}
                    />
                  }
                />
              </div>
            </div>
            <div className='d-flex mt-2'>
              <div className='w-50'>
                <Controller
                  name="last_entries_value"
                  control={control}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      type='number'
                      ref={field.ref}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 2,
                      }}
                      value={field.value}
                      label={`${t("itemcosts.fields.last_entries_value")}`}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.last_entries_value}
                      helperText={GetError(errors.last_entries_value?.type)}
                      size="small"
                      disabled={isEdit}
                    />
                  }
                />
              </div>
              <div className="w-50">
                <Controller
                  name="last_entries_date"
                  control={control}
                  render={({ field }) =>
                    <DatePicker
                      open={datePickerStates.last_entries_date}
                      onClose={() => handleCloseDatePicker('last_entries_date')}
                      ref={field.ref}
                      label={`${t("itemcosts.fields.last_entries_date")}`}
                      onChange={field.onChange}
                      slotProps={{
                        popper: {
                          sx: myStylePickers
                        },
                        textField: {
                          variant: "filled",
                          size: "small",
                          fullWidth: true,
                          onClick: () => handleOpenDatePicker('last_entries_date'),
                          onBeforeInput: disableKeyboardEntry,
                          error: !!errors.last_entries_date,
                          helperText: GetError(errors.last_entries_date?.type),
                        }
                      }}
                      format="DD/MM/YYYY"
                      disableFuture
                      disabled={isEdit}
                      defaultValue={defaultDateLastEntries ? dayjs((defaultDateLastEntries.toString())) : undefined}
                    />
                  }
                />
              </div>
            </div>
            <div className='d-flex mt-2'>
              <div className='w-50'>
                <Controller
                  name="last_outings_value"
                  control={control}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      type='number'
                      ref={field.ref}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                        "& input[type=number]": { MozAppearance: "textfield", }, width: "100%", paddingRight: 2,
                      }}
                      value={field.value}
                      label={`${t("itemcosts.fields.last_outings_value")}`}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.last_outings_value}
                      helperText={GetError(errors.last_outings_value?.type)}
                      size="small"
                      disabled={isEdit}
                    />
                  }
                />
              </div>
              <div className="w-50">
                <Controller
                  name="last_outings_date"
                  control={control}
                  render={({ field }) =>
                    <DatePicker
                      open={datePickerStates.last_outings_date}
                      onClose={() => handleCloseDatePicker('last_outings_date')}
                      ref={field.ref}
                      label={`${t("itemcosts.fields.last_outings_date")}`}
                      onChange={field.onChange}
                      slotProps={{
                        popper: {
                          sx: myStylePickers
                        },
                        textField: {
                          variant: "filled",
                          size: "small",
                          fullWidth: true,
                          onClick: () => handleOpenDatePicker('last_outings_date'),
                          onBeforeInput: disableKeyboardEntry,
                          error: !!errors.last_outings_date,
                          helperText: GetError(errors.last_outings_date?.type),
                        }
                      }}
                      format="DD/MM/YYYY"
                      disableFuture
                      disabled={isEdit}
                      defaultValue={defaultDateLastOutings ? dayjs((defaultDateLastOutings.toString())) : undefined}
                    />
                  }
                />
              </div>
            </div>

            <div className='d-flex flex-row-reverse mt-1'>
              <ButtonLoading isLoading={loadingPost} onClick={() => { onUpdateData(getValues()) }} variant='outlined' className='m-1' disabled={loadingPost}>{t("generic.buttons.update")}</ButtonLoading>
            </div>

          </form>

        </Box>
      }
      <SnackbarComponent />
    </>
  )
}
